/**
 * eBook Service
 * ==============
 * API service for eBook upload and management
 */

import authService from './authService';
import { API_BASE_URL } from './apiConfig';

const API = API_BASE_URL;

const ebookService = {
  /**
   * Upload an eBook file
   */
  async uploadEbook(submissionId, file, onProgress = null) {
    const token = authService.getAccessToken();
    
    const formData = new FormData();
    formData.append('file', file);
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.open('POST', `${API}/publisher/submissions/${submissionId}/ebook`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      
      // Progress tracking
      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            onProgress(percentComplete);
          }
        };
      }
      
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            reject(new Error('Invalid response'));
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            reject(new Error(error.detail || 'Upload failed'));
          } catch (e) {
            reject(new Error('Upload failed'));
          }
        }
      };
      
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.onabort = () => reject(new Error('Upload cancelled'));
      
      xhr.send(formData);
    });
  },
  
  /**
   * List eBooks for a submission
   */
  async listEbooks(submissionId) {
    const token = authService.getAccessToken();
    const response = await fetch(
      `${API}/publisher/submissions/${submissionId}/ebook`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to list ebooks');
    }
    
    return response.json();
  },
  
  /**
   * Delete an eBook
   */
  async deleteEbook(submissionId, assetId) {
    const token = authService.getAccessToken();
    const response = await fetch(
      `${API}/publisher/submissions/${submissionId}/ebook/${assetId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to delete ebook');
    }
    
    return response.json();
  },
  
  /**
   * Get file stream URL
   */
  getStreamUrl(assetId) {
    return `${API}/files/${assetId}/stream`;
  },
  
  /**
   * Get file download URL
   */
  getDownloadUrl(assetId) {
    return `${API}/files/${assetId}/download`;
  },
  
  /**
   * Get preview thumbnail URL
   */
  getPreviewUrl(assetId) {
    return `${API}/files/${assetId}/preview`;
  },
  
  /**
   * Get file info
   */
  async getFileInfo(assetId) {
    const token = authService.getAccessToken();
    const response = await fetch(
      `${API}/files/${assetId}/info`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to get file info');
    }
    
    return response.json();
  },
  
  /**
   * Format file size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },
  
  /**
   * Format checksum for display (first 6 + last 6 chars)
   */
  formatChecksum(checksum) {
    if (!checksum || checksum.length < 12) return checksum;
    return `${checksum.slice(0, 6)}...${checksum.slice(-6)}`;
  },
  
  /**
   * Get file icon based on extension
   */
  getFileIcon(ext) {
    const icons = {
      '.pdf': 'file-pdf',
      '.epub': 'book-open',
      '.mobi': 'book'
    };
    return icons[ext] || 'file';
  },
  
  /**
   * Admin: List ebooks for a submission
   */
  async adminListEbooks(submissionId) {
    const token = authService.getAccessToken();
    const response = await fetch(
      `${API}/admin/submissions/${submissionId}/ebooks`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to list ebooks');
    }
    
    return response.json();
  },
  
  /**
   * Admin: Verify an ebook
   */
  async adminVerifyEbook(submissionId, assetId) {
    const token = authService.getAccessToken();
    const response = await fetch(
      `${API}/admin/submissions/${submissionId}/ebook/${assetId}/verify`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to verify ebook');
    }
    
    return response.json();
  }
};

export default ebookService;
